import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BellIcon,
  CheckIcon,
  EyeIcon,
  TrashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  InboxIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChatBubbleLeftIcon,
  ClipboardDocumentCheckIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NotificationsCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  useEffect(() => {
    // Simulate fetching notifications data
    const fetchNotifications = () => {
      const mockNotifications = [
        {
          id: 'NOTIF001',
          title: 'Task Assignment: Patient Registration System Update',
          message: 'You have been assigned a new task for updating the patient registration system. The deadline is tomorrow.',
          category: 'tasks',
          type: 'assignment',
          priority: 'high',
          status: 'unread',
          timestamp: '2025-01-23T14:30:00Z',
          sender: {
            name: 'Dr. Sarah Johnson',
            avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=3b82f6&color=fff',
            role: 'Project Manager'
          },
          actionUrl: '/tasks/45',
          actionText: 'View Task',
          relatedModule: 'tasks'
        },
        {
          id: 'NOTIF002',
          title: 'Document Review Required: Clinical Protocol Update',
          message: 'A new version of the clinical protocol document requires your review and approval.',
          category: 'documents',
          type: 'review_request',
          priority: 'medium',
          status: 'unread',
          timestamp: '2025-01-23T13:15:00Z',
          sender: {
            name: 'Dr. Michael Chen',
            avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=10b981&color=fff',
            role: 'Medical Director'
          },
          actionUrl: '/documents/123',
          actionText: 'Review Document',
          relatedModule: 'documents'
        },
        {
          id: 'NOTIF003',
          title: 'Event Reminder: Monthly Team Meeting',
          message: 'Reminder: The monthly team meeting is scheduled for tomorrow at 2:00 PM in Conference Room A.',
          category: 'events',
          type: 'reminder',
          priority: 'medium',
          status: 'read',
          timestamp: '2025-01-23T12:00:00Z',
          sender: {
            name: 'Jennifer Wu',
            avatar: 'https://ui-avatars.com/api/?name=Jennifer+Wu&background=f59e0b&color=fff',
            role: 'HR Manager'
          },
          actionUrl: '/events/67',
          actionText: 'View Event',
          relatedModule: 'events'
        },
        {
          id: 'NOTIF004',
          title: 'Training Course Available: Emergency Response Protocols',
          message: 'A new mandatory training course on emergency response protocols is now available. Please complete by end of week.',
          category: 'training',
          type: 'course_available',
          priority: 'high',
          status: 'unread',
          timestamp: '2025-01-23T11:45:00Z',
          sender: {
            name: 'Training System',
            avatar: 'https://ui-avatars.com/api/?name=Training+System&background=8b5cf6&color=fff',
            role: 'System'
          },
          actionUrl: '/training/course/89',
          actionText: 'Start Course',
          relatedModule: 'training'
        },
        {
          id: 'NOTIF005',
          title: 'Form Submission: Equipment Request Approved',
          message: 'Your equipment request form has been approved. The new medical equipment will be delivered next week.',
          category: 'forms',
          type: 'approval',
          priority: 'low',
          status: 'read',
          timestamp: '2025-01-23T10:30:00Z',
          sender: {
            name: 'Lisa Rodriguez',
            avatar: 'https://ui-avatars.com/api/?name=Lisa+Rodriguez&background=ef4444&color=fff',
            role: 'Operations Manager'
          },
          actionUrl: '/forms/submissions/34',
          actionText: 'View Submission',
          relatedModule: 'forms'
        },
        {
          id: 'NOTIF006',
          title: 'Support Ticket Update: IT Help Request #2341',
          message: 'Your IT support ticket has been updated. Our technician has responded with a solution.',
          category: 'support',
          type: 'ticket_update',
          priority: 'medium',
          status: 'unread',
          timestamp: '2025-01-23T09:15:00Z',
          sender: {
            name: 'IT Support Team',
            avatar: 'https://ui-avatars.com/api/?name=IT+Support&background=06b6d4&color=fff',
            role: 'Support'
          },
          actionUrl: '/support/tickets/2341',
          actionText: 'View Ticket',
          relatedModule: 'support'
        },
        {
          id: 'NOTIF007',
          title: 'Payroll Information: Pay Stub Available',
          message: 'Your pay stub for January 2025 is now available for download in the payroll section.',
          category: 'payroll',
          type: 'document_available',
          priority: 'low',
          status: 'read',
          timestamp: '2025-01-22T16:00:00Z',
          sender: {
            name: 'Payroll System',
            avatar: 'https://ui-avatars.com/api/?name=Payroll+System&background=f97316&color=fff',
            role: 'System'
          },
          actionUrl: '/payroll/payslip',
          actionText: 'View Pay Stub',
          relatedModule: 'payroll'
        },
        {
          id: 'NOTIF008',
          title: 'Forum Discussion: Reply to Your Post',
          message: 'Dr. Emily Davis replied to your post in the "Patient Care Best Practices" discussion forum.',
          category: 'forums',
          type: 'reply',
          priority: 'low',
          status: 'read',
          timestamp: '2025-01-22T14:20:00Z',
          sender: {
            name: 'Dr. Emily Davis',
            avatar: 'https://ui-avatars.com/api/?name=Emily+Davis&background=84cc16&color=fff',
            role: 'Senior Physician'
          },
          actionUrl: '/forums/thread/158',
          actionText: 'View Discussion',
          relatedModule: 'forums'
        }
      ];

      setNotifications(mockNotifications);
      setLoading(false);
    };

    setTimeout(fetchNotifications, 500);
  }, []);

  const categories = [
    { id: 'all', name: 'All Notifications', icon: BellIcon, count: notifications.length },
    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentCheckIcon, count: notifications.filter(n => n.category === 'tasks').length },
    { id: 'documents', name: 'Documents', icon: DocumentTextIcon, count: notifications.filter(n => n.category === 'documents').length },
    { id: 'events', name: 'Events', icon: CalendarIcon, count: notifications.filter(n => n.category === 'events').length },
    { id: 'training', name: 'Training', icon: CheckCircleIcon, count: notifications.filter(n => n.category === 'training').length },
    { id: 'forms', name: 'Forms', icon: DocumentTextIcon, count: notifications.filter(n => n.category === 'forms').length },
    { id: 'support', name: 'Support', icon: InformationCircleIcon, count: notifications.filter(n => n.category === 'support').length },
    { id: 'payroll', name: 'Payroll', icon: UserIcon, count: notifications.filter(n => n.category === 'payroll').length },
    { id: 'forums', name: 'Forums', icon: ChatBubbleLeftIcon, count: notifications.filter(n => n.category === 'forums').length }
  ];

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.sender.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || notification.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || notification.status === selectedStatus;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'sender':
        comparison = a.sender.name.localeCompare(b.sender.name);
        break;
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      default: // date
        comparison = new Date(a.timestamp) - new Date(b.timestamp);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'assignment':
        return <ClipboardDocumentCheckIcon className="h-5 w-5" />;
      case 'review_request':
        return <EyeIcon className="h-5 w-5" />;
      case 'reminder':
        return <ClockIcon className="h-5 w-5" />;
      case 'course_available':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'approval':
        return <CheckIcon className="h-5 w-5" />;
      case 'ticket_update':
        return <InformationCircleIcon className="h-5 w-5" />;
      case 'document_available':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'reply':
        return <ChatBubbleLeftIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, status: 'read' }
          : notification
      )
    );
  };

  const markAsUnread = (notificationId) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, status: 'unread' }
          : notification
      )
    );
  };

  const deleteNotification = (notificationId) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== notificationId)
    );
    setSelectedNotifications(prev =>
      prev.filter(id => id !== notificationId)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, status: 'read' }))
    );
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev =>
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === sortedNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(sortedNotifications.map(n => n.id));
    }
  };

  const bulkMarkAsRead = () => {
    setNotifications(prev =>
      prev.map(notification =>
        selectedNotifications.includes(notification.id)
          ? { ...notification, status: 'read' }
          : notification
      )
    );
    setSelectedNotifications([]);
  };

  const bulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notifications?`)) {
      setNotifications(prev =>
        prev.filter(notification => !selectedNotifications.includes(notification.id))
      );
      setSelectedNotifications([]);
    }
  };

  const unreadCount = notifications.filter(n => n.status === 'unread').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <BellIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notifications Center</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Stay updated with important messages and alerts
                    {unreadCount > 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {unreadCount} unread
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Mark All Read
                </button>
                <Link
                  to="/notifications/settings"
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Categories</h2>
              <nav className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`${
                      selectedCategory === category.id
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium border rounded-md`}
                  >
                    <div className="flex items-center">
                      <category.icon className="h-5 w-5 mr-3" />
                      <span className="truncate">{category.name}</span>
                    </div>
                    {category.count > 0 && (
                      <span className={`${
                        selectedCategory === category.id ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                      } inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-2 flex-shrink-0`}>
                        {category.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search and Filters */}
            <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                {/* Search */}
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Search notifications..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center space-x-4">
                  {/* Filters Toggle */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    Filters
                  </button>

                  {/* Sort */}
                  <div className="relative">
                    <select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [field, order] = e.target.value.split('-');
                        setSortBy(field);
                        setSortOrder(order);
                      }}
                      className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="priority-desc">High Priority First</option>
                      <option value="title-asc">Title A-Z</option>
                      <option value="sender-asc">Sender A-Z</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <ChevronDownIcon className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Status</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                      </select>
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setSelectedStatus('all');
                          setSearchTerm('');
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Actions */}
            {selectedNotifications.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedNotifications.length} notification{selectedNotifications.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={bulkMarkAsRead}
                      className="inline-flex items-center px-3 py-1 border border-blue-300 shadow-sm text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Mark as Read
                    </button>
                    <button
                      onClick={bulkDelete}
                      className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm font-medium rounded text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications List */}
            {sortedNotifications.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <InboxIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No notifications found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : "You're all caught up! No new notifications."
                  }
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Table Header */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.length === sortedNotifications.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {sortedNotifications.length} notification{sortedNotifications.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Notifications */}
                <div className="divide-y divide-gray-200">
                  {sortedNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-6 hover:bg-gray-50 ${
                        notification.status === 'unread' ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notification.id)}
                          onChange={() => handleSelectNotification(notification.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                        />
                        
                        <div className="ml-4 flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start flex-1">
                              <div className={`p-2 rounded-lg mr-4 ${
                                notification.status === 'unread' ? 'bg-blue-100' : 'bg-gray-100'
                              }`}>
                                {getTypeIcon(notification.type)}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h3 className={`text-sm font-medium ${
                                    notification.status === 'unread' ? 'text-gray-900' : 'text-gray-700'
                                  } truncate`}>
                                    {notification.title}
                                  </h3>
                                  <div className="flex items-center space-x-2 ml-4">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                                      {notification.priority.toUpperCase()}
                                    </span>
                                    {notification.status === 'unread' && (
                                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                                
                                <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                                  {notification.message}
                                </p>
                                
                                <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                                  <div className="flex items-center">
                                    <img
                                      className="h-4 w-4 rounded-full mr-1"
                                      src={notification.sender.avatar}
                                      alt={notification.sender.name}
                                    />
                                    {notification.sender.name}
                                  </div>
                                  <span>{formatTimestamp(notification.timestamp)}</span>
                                  <span className="capitalize">{notification.category}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {notification.actionUrl && (
                                <Link
                                  to={notification.actionUrl}
                                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  {notification.actionText}
                                </Link>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              {notification.status === 'unread' ? (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                  title="Mark as read"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => markAsUnread(notification.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                  title="Mark as unread"
                                >
                                  <ArrowPathIcon className="h-4 w-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default NotificationsCenter;
